import { useState } from 'react';
import { useApiResource } from '@/hooks/useApiResource';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { FolderKanban, ChevronRight, Trash2 } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { AppHeader } from '@/components/AppHeader';
import { Chip, ProgressBar, EmptyState } from '@/components/Primitives';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { FAB, FormDrawer, TextField, TextAreaField, SelectField, Field } from '@/components/FormControls';
import { Slider } from '@/components/ui/slider';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { PROJECT_CATEGORIES, PROJECT_STATUSES, categoryChip, statusChip } from '@/lib/format';
import { useLang } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

const emptyProject = {
  name: '', description: '', category: 'Business', status: 'Planning', progress: 0, nextAction: '',
};
const cardEnter = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };

export default function Projects() {
  const navigate = useNavigate();
  const { t, tEnum } = useLang();
  const { data, loading, reload: load } = useApiResource(
    () => api.get('/projects').then((r) => r.data),
    { errorMessage: t('projects.loadError') }
  );
  const projects = data || [];
  const [filter, setFilter] = useState('All');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const openCreate = () => { setEditing(null); setForm(emptyProject); setDrawerOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...emptyProject, ...p }); setDrawerOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('projects.nameRequired')); return; }
    setSaving(true);
    try {
      const payload = { ...form, progress: Number(form.progress) || 0 };
      if (editing) { await api.put(`/projects/${editing.id}`, payload); toast.success(t('projects.updated')); }
      else { await api.post('/projects', payload); toast.success(t('projects.created')); }
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(t('projects.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/projects/${deleteId}`);
      toast.success(t('projects.deleted'));
      setDeleteId(null);
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(t('projects.deleteError'));
    }
  };

  const filters = ['All', 'Planning', 'Active', 'Waiting', 'Completed'];
  const filtered = projects.filter((p) => (filter === 'All' ? true : p.status === filter));

  if (loading) return <FullScreenLoader label={t('projects.loader')} />;

  return (
    <AppLayout>
      <AppHeader title={t('projects.title')} subtitle={t('projects.subtitle')} />

      <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto pb-1">
        {filters.map((f) => (
          <button
            key={f}
            data-testid={`projects-filter-${f.toLowerCase()}-chip`}
            onClick={() => setFilter(f)}
            className={cn(
              'shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors',
              filter === f
                ? 'border-brand-cyan/40 bg-brand-cyan/15 text-cyan-100'
                : 'border-white/10 bg-white/[0.05] text-foreground/60'
            )}
          >
            {tEnum(f)}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {filtered.length === 0 ? (
          <EmptyState icon={FolderKanban} title={t('projects.emptyTitle')} subtitle={t('projects.emptySubtitle')} />
        ) : (
          filtered.map((p, i) => (
            <motion.div
              key={p.id}
              initial={cardEnter.initial}
              animate={cardEnter.animate}
              transition={{ duration: 0.3, delay: i * 0.03 }}
              data-testid={`project-card-${p.id}`}
              className="glass-card rounded-2xl p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <Chip className={categoryChip(p.category)}>{tEnum(p.category)}</Chip>
                  <h3 className="mt-2 text-sm font-semibold text-foreground/90">{p.name}</h3>
                </div>
                <Chip className={statusChip(p.status)}>{tEnum(p.status)}</Chip>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-foreground/55">
                <span>{t('common.progress')}</span>
                <span className="font-mono-plex text-brand-cyan">{p.progress}%</span>
              </div>
              <ProgressBar value={p.progress} className="mt-1.5" />
              <p className="mt-2.5 line-clamp-1 text-xs text-foreground/60">
                <span className="text-brand-cyan">{t('common.next')}</span> {p.nextAction || '\u2014'}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  data-testid={`project-open-${p.id}`}
                  onClick={() => navigate(`/projects/${p.id}`)}
                  className="flex flex-1 items-center justify-center gap-1 rounded-xl bg-white/[0.08] py-2 text-sm font-medium text-foreground hover:bg-white/[0.12]"
                >
                  {t('common.open')} <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => openEdit(p)}
                  className="rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-white/[0.1]"
                >
                  {t('common.edit')}
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>

      <FAB onClick={openCreate} testId="projects-add-fab" label={t('projects.addFab')} />

      <FormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={editing ? t('projects.editTitle') : t('projects.newTitle')}
        onSubmit={save}
        loading={saving}
        submitLabel={editing ? t('common.saveChanges') : t('projects.create')}
        submitTestId="project-create-save-button"
      >
        <TextField
          label={t('projects.name')}
          testId="project-name-input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder={t('projects.namePlaceholder')}
        />
        <div className="grid grid-cols-2 gap-3">
          <SelectField label={t('projects.category')} testId="project-category-select" value={form.category} onValueChange={(v) => setForm({ ...form, category: v })} options={PROJECT_CATEGORIES} />
          <SelectField label={t('projects.status')} testId="project-status-select" value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} options={PROJECT_STATUSES} />
        </div>
        <TextAreaField label={t('projects.description')} testId="project-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t('projects.descriptionPlaceholder')} />
        <TextField label={t('projects.nextAction')} testId="project-nextaction-input" value={form.nextAction} onChange={(e) => setForm({ ...form, nextAction: e.target.value })} placeholder={t('projects.nextActionPlaceholder')} />
        <Field label={t('projects.progress', { n: form.progress })}>
          <Slider data-testid="project-progress-slider" value={[Number(form.progress) || 0]} onValueChange={(v) => setForm({ ...form, progress: v[0] })} max={100} step={5} className="py-2" />
        </Field>
        {editing ? (
          <button data-testid="project-delete-button" onClick={() => setDeleteId(editing.id)} className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-rose-300/20 bg-rose-400/10 px-4 py-2.5 text-sm font-medium text-rose-200 hover:bg-rose-400/15">
            <Trash2 className="h-4 w-4" /> {t('projects.deleteProject')}
          </button>
        ) : null}
      </FormDrawer>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="mx-auto max-w-[420px] border-white/10 bg-[#0A1730] text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('projects.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-foreground/60">{t('projects.confirmDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-white/[0.06] text-foreground hover:bg-white/[0.1]">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction data-testid="project-delete-confirm" onClick={doDelete} className="bg-rose-500 text-white hover:bg-rose-500/90">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
